import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2, User, Stethoscope, IndianRupee } from "lucide-react";
import { toast } from 'sonner';
import { hospitalAdminApi } from "@/api/hospitalAdmin";
import { billingApi } from "@/api/billing";
import { appointmentApi } from "@/api/appointment";
import { paymentApi } from "@/api/payment";
import { patientApi } from "@/api/patient";
import { HospitalStaff, Appointment, BillCreateData, BillType, BillStatus, AppointmentStatus, PaymentMethod, PaymentCreateData } from "@/types/types";
import PaymentSplitsEditor, { getSplitsError, getSplitsTotal, PaymentSplitDraft } from "@/components/billing/PaymentSplitsEditor";
import { useAuth } from "@/contexts/AuthContext";
import { istDateInputToIso, toIstDateInputValue } from "@/utils/dateUtils";
import { canBackdateOpd as userCanBackdateOpd } from "@/lib/opdBackdateAccess";
import { patientNeedsRegistrationSourceCapture } from "@/lib/patientRegistrationSource";
import RegistrationSourceCaptureDialog from "@/components/patient/RegistrationSourceCaptureDialog";

function isPatientDemographicsIncomplete(patient?: {
    gender?: string | null;
    dob?: string | Date | null;
    registrationSource?: string | null;
} | null): boolean {
    if (!patient) return false;
    if (!patient.gender) return true;
    if (!patient.dob) return true;
    if (String(patient.registrationSource || "").toUpperCase() === "PRACTO") {
        const d = new Date(patient.dob);
        if (
            !Number.isNaN(d.getTime()) &&
            d.getUTCFullYear() === 1970 &&
            d.getUTCMonth() === 0 &&
            d.getUTCDate() === 1
        ) {
            return true;
        }
    }
    return false;
}

interface CreateAppointmentBillProps {
    appointmentId: string;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export default function CreateAppointmentBill({
    appointmentId,
    isOpen,
    onClose,
    onSuccess
}: CreateAppointmentBillProps) {
    if (appointmentId === "" || !isOpen || !appointmentId) return;
    const { user, isChainAdmin } = useAuth();
    const canBackdateOpd = userCanBackdateOpd(user, isChainAdmin);
    const [opdCharge, setOpdCharge] = useState<number>(0);
    const [discountPercentage, setDiscountPercentage] = useState<number>(0);
    const [customDiscountAmount, setCustomDiscountAmount] = useState<number>(0);
    const [discountType, setDiscountType] = useState<'percentage' | 'custom'>('percentage');
    const [splits, setSplits] = useState<PaymentSplitDraft[]>([{ mode: PaymentMethod.CASH, amount: "" }]);
    const [notes, setNotes] = useState<string>("");
    const [processingStep, setProcessingStep] = useState<'form' | 'processing' | 'complete'>('form');
    const [paymentOption, setPaymentOption] = useState<'none' | 'full' | 'partial'>('none');
    const [patientGender, setPatientGender] = useState<string>("");
    const [patientDob, setPatientDob] = useState<string>("");
    const [captureDialogOpen, setCaptureDialogOpen] = useState(false);
    const [billDateLocal, setBillDateLocal] = useState(() => toIstDateInputValue(new Date()));
    const queryClient = useQueryClient();

    // Fetch appointment details
    const { data: appointment, isLoading: appointmentLoading } = useQuery<Appointment>({
        queryKey: ["appointment", appointmentId],
        queryFn: async () => {
            const response = await appointmentApi.getAppointmentById(appointmentId);
            return response.data?.data;
        },
        enabled: !!appointmentId && isOpen,
    });

    const needsDemographics = isPatientDemographicsIncomplete(appointment?.patient);

    const { data: patientRecord } = useQuery({
        queryKey: ["patient", appointment?.patientId],
        queryFn: () => patientApi.getPatientById(appointment!.patientId),
        enabled: !!appointment?.patientId && isOpen,
    });

    const needsRegistrationSourceCapture = patientNeedsRegistrationSourceCapture(
        patientRecord ?? appointment?.patient
    );

    useEffect(() => {
        if (!appointment?.patient || !needsDemographics) return;
        const g = appointment.patient.gender;
        if (g && !(String(appointment.patient.registrationSource || "").toUpperCase() === "PRACTO" && !appointment.patient.dob)) {
            // keep empty when incomplete placeholder
        }
        setPatientGender("");
        setPatientDob("");
    }, [appointment?.patient?.id, needsDemographics]);

    // Fetch doctor details using getStaffById
    const { data: doctor, isLoading: doctorLoading } = useQuery<HospitalStaff>({
        queryKey: ["doctor", appointment?.doctorId],
        queryFn: async () => {
            if (!appointment?.doctorId) throw new Error("No doctor ID");
            return await hospitalAdminApi.getStaffById(appointment.doctorId);
        },
        enabled: !!appointment?.doctorId && isOpen,
    });

    // Set OPD charge when doctor data is loaded
    useEffect(() => {
        if (doctor?.opdCharge?.amount) {
            setOpdCharge(doctor.opdCharge.amount);
        }
    }, [doctor]);

    useEffect(() => {
        if (!isOpen) return;
        if (appointment?.scheduledAt) {
            setBillDateLocal(toIstDateInputValue(appointment.scheduledAt));
        } else {
            setBillDateLocal(toIstDateInputValue(new Date()));
        }
    }, [isOpen, appointment?.scheduledAt]);

    // Process payment mutation
    const processPaymentMutation = useMutation({
        mutationFn: async (payments: PaymentCreateData[]) => paymentApi.bulkProcessPayments(payments),
        onError: (error: any) => {
            console.error('Error processing payment:', error);
            toast.error(error.response?.data?.message || 'Failed to process payment');
            setProcessingStep('form');
        },
    });

    // Create bill mutation
    const createBillMutation = useMutation({
        mutationFn: async (billData: BillCreateData) => {
            return await billingApi.generateAppointmentBill(appointmentId, billData);
        },
        onSuccess: () => {
            // Invalidate all appointment queries to refresh the queue management
            queryClient.invalidateQueries({ queryKey: ['appointments'] });
            queryClient.invalidateQueries({ queryKey: ['bills'] });
            queryClient.invalidateQueries({ queryKey: ['payments'] });
            queryClient.invalidateQueries({ queryKey: ['doctors'] });
            toast.success('Bill created and payment processed successfully');
            setProcessingStep('complete');
            onClose();
            resetForm();
        },
        onError: (error: any) => {
            console.error('Error creating bill:', error);
            toast.error(error.response?.data?.message || 'Failed to create bill');
            setProcessingStep('form');
        },
    });

    const updateAppointmentStatusMutation = useMutation({
        mutationFn: async (appointmentId: string) => {
            return await appointmentApi.updateAppointmentStatus(appointmentId, AppointmentStatus.CONFIRMED);
        },
        onSuccess: () => {
            // Invalidate appointment queries to refresh the queue management
            queryClient.invalidateQueries({ queryKey: ['appointments'] });
            toast.success('Appointment status updated successfully');
        },
        onError: (error: any) => {
            console.error('Error updating appointment status:', error);
            toast.error(error.response?.data?.message || 'Failed to update appointment status');
        },
    });

    const resetForm = () => {
        setDiscountPercentage(0);
        setCustomDiscountAmount(0);
        setDiscountType('percentage');
        setSplits([{ mode: PaymentMethod.CASH, amount: "" }]);
        setNotes("");
        setProcessingStep('form');
        setPaymentOption('none'); // Reset payment option
        setOpdCharge(0);
        setPatientGender("");
        setPatientDob("");
        setBillDateLocal(toIstDateInputValue(new Date()));
    };

    const handleSubmit = async () => {

        if (!appointment?.patientId || !appointment?.hospitalId) {
            toast.error('Missing appointment details');
            return;
        }

        if (needsRegistrationSourceCapture) {
            setCaptureDialogOpen(true);
            return;
        }

        await processBillCreation();
    };

    const processBillCreation = async () => {
        if (!appointment?.patientId || !appointment?.hospitalId) {
            return;
        }

        const tier = String(appointment?.opdConsultationTier ?? "").toUpperCase();
        const doctorListFee = Number(doctor?.opdCharge?.amount ?? opdCharge);
        const autoZeroOpd =
            tier === "FREE" ||
            (Number.isFinite(doctorListFee) && doctorListFee <= 0 && opdCharge <= 0);
        if (autoZeroOpd) {
            try {
                setProcessingStep("processing");
                await appointmentApi.confirmFreeCrmOpd(appointmentId);
                queryClient.invalidateQueries({ queryKey: ["appointments"] });
                queryClient.invalidateQueries({ queryKey: ["appointments", "by-date"] });
                toast.success("OPD confirmed — zero-value bill created");
                onSuccess();
                onClose();
            } catch (error: any) {
                toast.error(
                    error.response?.data?.message ||
                        error.message ||
                        "Could not confirm zero OPD"
                );
                setProcessingStep("form");
            }
            return;
        }

        if (needsDemographics) {
            if (!patientGender || !patientDob) {
                toast.error('Please enter patient gender and date of birth before billing');
                return;
            }
        }

        // Calculate discount amount
        let discountAmount = 0;
        if (discountType === 'percentage' && discountPercentage > 0) {
            discountAmount = (opdCharge * discountPercentage) / 100;
        } else if (discountType === 'custom' && customDiscountAmount > 0) {
            discountAmount = customDiscountAmount;
        }

        if (discountAmount > opdCharge) {
            discountAmount = opdCharge;
        }

        const totalAmount = opdCharge - discountAmount;

        if (totalAmount < 0) {
            toast.error('Invalid discount: total cannot be negative');
            return;
        }

        if (totalAmount > 0 && opdCharge <= 0) {
            toast.error('OPD charge must be greater than 0 when the bill total is not zero');
            return;
        }

        const needsPayment = totalAmount > 0;
        if (needsPayment && paymentOption === "none") {
            toast.error("Select a payment option");
            return;
        }

        if (needsPayment) {
            const splitsErr = getSplitsError({ splits, maxTotal: totalAmount });
            if (splitsErr) {
                toast.error(splitsErr);
                return;
            }
        }

        setProcessingStep('processing');

        try {
            if (needsDemographics) {
                await patientApi.updatePatientDetails(appointment.patientId, {
                    gender: patientGender as 'MALE' | 'FEMALE' | 'OTHER',
                    dob: patientDob,
                });
                queryClient.invalidateQueries({ queryKey: ["appointment", appointmentId] });
            }

            if (canBackdateOpd && !billDateLocal.trim()) {
                toast.error("Please select a bill date");
                setProcessingStep('form');
                return;
            }

            const resolvedBillDate = canBackdateOpd
                ? new Date(istDateInputToIso(billDateLocal))
                : new Date();

            const isZeroTotal = totalAmount <= 0.01;
            const billData: BillCreateData = {
                patientId: appointment.patientId,
                hospitalId: appointment.hospitalId,
                appointmentId: appointmentId,
                paidAmount: 0,
                dueAmount: Number(totalAmount),
                status: isZeroTotal ? BillStatus.PAID : BillStatus.DRAFT,
                billDate: resolvedBillDate,
                dueDate: resolvedBillDate,
                items: [
                    {
                        itemType: BillType.OPD_CONSULTATION,
                        description: `OPD Consultation - Dr ${doctor?.name} (${doctor?.specialisation})`,
                        quantity: 1,
                        unitPrice: Number(opdCharge),
                        totalPrice: Math.max(0, Number(opdCharge) - Number(discountAmount)),
                        discountAmount: Number(discountAmount),
                        notes: notes || undefined,
                    }
                ],
                notes: notes || undefined,
            };

            const billResponse = await createBillMutation.mutateAsync(billData);
            const billId = billResponse.data?.data?.id;

            if (!billId) {
                throw new Error('Failed to create bill');
            }

            const amountPaidNow = getSplitsTotal(splits);
            if (amountPaidNow > 0) {
                const payments: PaymentCreateData[] = splits.map((sp) => ({
                    billId: billId,
                    amount: Number(sp.amount),
                    paymentMethod: sp.mode,
                    notes: notes || undefined,
                }));
                await processPaymentMutation.mutateAsync(payments);
            }

            updateAppointmentStatusMutation.mutate(appointmentId);
            onSuccess();

        } catch (error) {
            console.error('Error in bill creation process:', error);
            toast.error('Failed to complete the billing process');
            setProcessingStep('form');
        }
    };

    const isLoading = appointmentLoading || doctorLoading || createBillMutation.isPending || processPaymentMutation.isPending;
    const canOverrideBillAmount =
        user?.role === "RECEPTIONIST" ||
        user?.role === "HOSPITAL_ADMIN" ||
        user?.role === "SALES_MANAGER" ||
        user?.role === "SUPER_ADMIN";

    // Calculate values
    const discountAmount = discountType === 'percentage' && discountPercentage > 0
        ? (opdCharge * discountPercentage) / 100
        : discountType === 'custom' && customDiscountAmount > 0
            ? customDiscountAmount
            : 0;
    const totalAmount = opdCharge - discountAmount;
    const needsPayment = totalAmount > 0;
    const amountPaid = useMemo(() => (paymentOption === "none" ? 0 : getSplitsTotal(splits)), [paymentOption, splits]);
    const amountDue = totalAmount - amountPaid;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-lg font-medium">
                        Create Bill
                    </DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span className="text-sm text-gray-600">
                            {processingStep === 'processing' ? 'Processing...' : 'Loading...'}
                        </span>
                    </div>
                ) : processingStep === 'complete' ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                            <IndianRupee className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-base font-medium text-gray-900 mb-1">Success!</h3>
                        <p className="text-sm text-gray-500 text-center">
                            Bill created and payment processed.
                        </p>
                    </div>
                ) : (
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            void handleSubmit();
                        }}
                        className="space-y-6"
                    >
                        {needsRegistrationSourceCapture ? (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                This patient self-registered online. Registration source must be recorded before billing.
                            </p>
                        ) : null}
                        {/* Appointment Info */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                                <User className="h-4 w-4 text-gray-400" />
                                <div>
                                    <div className="font-medium text-sm">{appointment?.patient?.name}</div>
                                    <div className="text-xs text-gray-500">Patient</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Stethoscope className="h-4 w-4 text-gray-400" />
                                <div className="text-right">
                                    <div className="font-medium text-sm">{doctor?.name}</div>
                                    <div className="text-xs text-gray-500">{doctor?.specialisation}</div>
                                </div>
                            </div>
                        </div>

                        {needsDemographics && (
                            <div className="rounded-xl border border-amber-300 bg-amber-50/80 p-4 space-y-3">
                                <div>
                                    <h3 className="text-sm font-semibold text-amber-950">Complete patient registration</h3>
                                    <p className="text-xs text-amber-900/80 mt-0.5">
                                        Gender and date of birth are missing (e.g. Practo auto-registration). Ask the patient and save before billing.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1.5">
                                        <Label htmlFor="bill-patient-gender">Gender *</Label>
                                        <Select value={patientGender} onValueChange={setPatientGender}>
                                            <SelectTrigger id="bill-patient-gender">
                                                <SelectValue placeholder="Select gender" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="MALE">Male</SelectItem>
                                                <SelectItem value="FEMALE">Female</SelectItem>
                                                <SelectItem value="OTHER">Other</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label htmlFor="bill-patient-dob">Date of birth *</Label>
                                        <Input
                                            id="bill-patient-dob"
                                            type="date"
                                            value={patientDob}
                                            onChange={(e) => setPatientDob(e.target.value)}
                                            max={new Date().toISOString().slice(0, 10)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {canBackdateOpd ? (
                            <div className="space-y-2 rounded-lg border bg-muted/30 p-4">
                                <Label htmlFor="opd-bill-date">Bill date</Label>
                                <Input
                                    id="opd-bill-date"
                                    type="date"
                                    value={billDateLocal}
                                    max={toIstDateInputValue(new Date())}
                                    onChange={(e) => setBillDateLocal(e.target.value)}
                                />
                                <p className="text-xs text-muted-foreground">
                                    Defaults to the appointment date. Chain admin can back-date the OPD bill (today or earlier).
                                </p>
                            </div>
                        ) : null}

                        {/* OPD Charge & Discount Section */}
                        <div className="bg-white border rounded-xl shadow-sm p-6 mb-6">
                            {canOverrideBillAmount && (
                                <div className="mb-4">
                                    <Label className="text-sm font-medium">OPD Charge</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={Number.isFinite(opdCharge) ? opdCharge : 0}
                                        onChange={(e) => setOpdCharge(Number(e.target.value))}
                                        disabled={createBillMutation.isPending}
                                        className="mt-1"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Reception can override the consultation amount before creating the bill.
                                    </p>
                                </div>
                            )}
                            <div className="mb-4">
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Apply Discount</h3>
                                <div className="flex gap-6 mb-4">
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={discountType === 'percentage'}
                                            onChange={() => setDiscountType('percentage')}
                                            className="accent-blue-600"
                                        />
                                        Percentage (%)
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                        <input
                                            type="radio"
                                            checked={discountType === 'custom'}
                                            onChange={() => setDiscountType('custom')}
                                            className="accent-blue-600"
                                        />
                                        Custom Amount
                                    </label>
                                </div>
                                {discountType === 'percentage' ? (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1" htmlFor="discount-percentage">
                                            Discount Percentage
                                        </label>
                                        <Input
                                            id="discount-percentage"
                                            type="number"
                                            placeholder="e.g. 10"
                                            min="0"
                                            max="100"
                                            value={discountPercentage}
                                            onChange={e => setDiscountPercentage(Number(e.target.value))}
                                            className={`text-sm h-9 border-gray-300 rounded-md ${discountPercentage < 0 || discountPercentage > 100 ? 'border-red-500' : ''}`}
                                            disabled={createBillMutation.isPending}
                                        />
                                        <span className="text-xs text-gray-400">Enter a value between 0 and 100</span>
                                    </div>
                                ) : (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1" htmlFor="custom-discount">
                                            Custom Discount Amount
                                        </label>
                                        <Input
                                            id="custom-discount"
                                            type="number"
                                            placeholder={`Max: ₹${opdCharge}`}
                                            min="0"
                                            max={opdCharge}
                                            value={customDiscountAmount}
                                            onChange={e => setCustomDiscountAmount(Number(e.target.value))}
                                            className={`text-sm h-9 border-gray-300 rounded-md ${customDiscountAmount < 0 || customDiscountAmount > opdCharge ? 'border-red-500' : ''}`}
                                            disabled={createBillMutation.isPending}
                                        />
                                        <span className="text-xs text-gray-400">Enter a value up to the OPD fee</span>
                                    </div>
                                )}
                            </div>

                            {/* Price Summary */}
                            <div className="bg-gray-50 rounded-lg p-4 mt-4">
                                <div className="flex justify-between text-sm mb-1">
                                    <span>Base Price</span>
                                    <span>₹{opdCharge.toFixed(2)}</span>
                                </div>
                                {discountAmount > 0 && (
                                    <div className="flex justify-between text-sm text-green-600 mb-1">
                                        <span>Discount</span>
                                        <span>-₹{discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-lg font-bold mt-2">
                                    <span>Total</span>
                                    <span>₹{totalAmount.toFixed(2)}</span>
                                </div>
                            </div>
                        </div>

                        {/* Amount Details */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Payment Details</Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Paid & Buttons */}
                                <div>
                                    <div className="flex items-center gap-6 mb-2">
                                        <div>
                                            <Label className="text-xs text-gray-500">Total</Label>
                                            <div className="text-lg font-semibold">₹{totalAmount.toFixed(2)}</div>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-gray-500">Due</Label>
                                            <div className={`text-lg font-semibold ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>₹{amountDue.toFixed(2)}</div>
                                        </div>
                                    </div>
                                    {!needsPayment ? (
                                        <p className="text-sm text-muted-foreground rounded-md border bg-muted/40 px-3 py-2">
                                            Bill total is ₹0 (full discount). No payment is required.
                                        </p>
                                    ) : (
                                    <div className="flex flex-col gap-2">
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant={paymentOption === 'full' ? 'default' : 'outline'}
                                                className="flex-1"
                                                onClick={() => {
                                                    setPaymentOption('full');
                                                    setSplits([{ mode: PaymentMethod.CASH, amount: String(totalAmount) }]);
                                                }}
                                                disabled={createBillMutation.isPending}
                                            >
                                                Pay Full Amount
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={paymentOption === 'partial' ? 'default' : 'outline'}
                                                className="flex-1"
                                                onClick={() => {
                                                    setPaymentOption('partial');
                                                    setSplits([{ mode: PaymentMethod.CASH, amount: "" }]);
                                                }}
                                                disabled={createBillMutation.isPending}
                                            >
                                                Pay Partial Amount
                                            </Button>
                                        </div>
                                        {paymentOption !== 'none' && (
                                            <PaymentSplitsEditor
                                                splits={splits}
                                                onChange={setSplits}
                                                maxTotal={totalAmount}
                                                disabled={createBillMutation.isPending}
                                            />
                                        )}
                                    </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Notes (Optional)</Label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Additional notes..."
                                className="w-full h-20 p-2 text-sm border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
                                disabled={createBillMutation.isPending}
                            />
                        </div>

                        {/* Summary */}
                        <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                            <div className="flex justify-between text-sm">
                                <span>OPD Charge:</span>
                                <span>₹{opdCharge.toFixed(2)}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className="flex justify-between text-sm text-green-600">
                                    <span>Discount:</span>
                                    <span>-₹{discountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="border-t pt-2 flex justify-between font-semibold">
                                <span>Total:</span>
                                <span>₹{totalAmount.toFixed(2)}</span>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={createBillMutation.isPending}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={
                                    createBillMutation.isPending ||
                                    appointmentLoading ||
                                    doctorLoading ||
                                    (needsPayment && (!opdCharge || opdCharge <= 0)) ||
                                    (needsPayment && paymentOption === 'none') ||
                                    (needsPayment && !!getSplitsError({ splits, maxTotal: totalAmount }))
                                }
                                className="flex-1"
                            >
                                {createBillMutation.isPending ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Processing
                                    </>
                                ) : (
                                    'Create Bill'
                                )}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
            {appointment?.patientId ? (
                <RegistrationSourceCaptureDialog
                    open={captureDialogOpen}
                    onOpenChange={setCaptureDialogOpen}
                    patientId={appointment.patientId}
                    patientName={appointment.patient?.name}
                    onCaptured={() => {
                        void queryClient.invalidateQueries({ queryKey: ["patient", appointment.patientId] });
                        void processBillCreation();
                    }}
                />
            ) : null}
        </Dialog>
    );
}
